'use client'

import React, { useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { useDocumentationContext } from './documentation-provider'
import { parseTrainingGuide } from '@/utils/documentation-parser'
import { useDocumentationContent } from '@/hooks/useDocumentationContent'
import { cn } from '@/lib/utils'

export function DocumentationNav() {
  const { currentSection, currentArticle, openArticle } = useDocumentationContext()
  const { content, loading } = useDocumentationContent()
  
  // Parse the training guide into sections
  const sections = useMemo(() => {
    if (!content) return []
    return parseTrainingGuide(content)
  }, [content])

  if (loading) {
    return (
      <nav className="w-60 border-r border-gray-100 overflow-y-auto bg-gray-50">
        <div className="p-4 space-y-4">
          {/* Loading skeleton */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
              <div className="space-y-1">
                <div className="h-3 bg-gray-200 rounded w-full animate-pulse" />
                <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </nav>
    )
  }

  return (
    <nav className="w-60 border-r border-gray-100 overflow-y-auto bg-gray-50">
      <div className="p-4 space-y-5">
        {sections.map((section) => (
          <div key={section.id}>
            {/* Section Header */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm">{section.icon}</span>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.title}
              </h3>
            </div>
            
            {/* Articles */}
            <div className="space-y-1">
              {section.articles.map((article) => {
                const isActive = currentSection === section.id && currentArticle === article.id
                
                return (
                  <button
                    key={article.id}
                    onClick={() => openArticle(section.id, article.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm",
                      "transition-all duration-200",
                      "focus:outline-none focus:ring-1 focus:ring-purple-500",
                      "flex items-start justify-between group",
                      isActive ? [
                        "bg-purple-100",
                        "text-purple-700 font-medium"
                      ] : [
                        "text-gray-600",
                        "hover:bg-gray-100",
                        "hover:text-gray-900"
                      ]
                    )}
                  >
                    <span className="break-words pr-2 leading-tight">{article.title}</span>
                    {isActive && (
                      <ChevronRight className="h-3 w-3 text-purple-600 flex-shrink-0 mt-0.5" />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </nav>
  )
}